import {Inject, Injectable, PLATFORM_ID} from '@angular/core';
import {DOCUMENT} from '@angular/common';
import {isPlatformBrowser} from '@angular/common';

/**
 * Detects when the app is running as a sub-screen/section (e.g. inside a multi-view iframe)
 * so that toolbar, navbar and sidebar can be hidden for a minimal layout.
 *
 * Embedded mode is true when:
 * - Query param `embedded=1` or `embedded=true` is present, or
 * - The app is running inside an iframe (window.self !== window.top).
 */
@Injectable({
    providedIn: 'root'
})
export class EmbeddedLayoutService {
    private _isEmbedded: boolean | null = null;

    constructor(
        @Inject(PLATFORM_ID) private platformId: object,
        @Inject(DOCUMENT) private document: Document
    ) {}

    /**
     * True when the app should use minimal layout (no main toolbar, navbar, sidebar).
     * Cached after first check.
     */
    isEmbedded(): boolean {
        if (this._isEmbedded !== null) {
            return this._isEmbedded;
        }
        if (!isPlatformBrowser(this.platformId)) {
            this._isEmbedded = false;
            return false;
        }
        const search = this.document.defaultView?.location?.search ?? '';
        const params = new URLSearchParams(search);
        const embeddedParam = params.get('embedded');
        const fromParam = embeddedParam === '1' || embeddedParam === 'true';
        const win = this.document.defaultView as any;
        const inCypress = typeof win !== 'undefined' && 'Cypress' in win;
        const fromIframe = typeof win !== 'undefined' && win.self !== win.top && !inCypress;
        this._isEmbedded = fromParam || fromIframe;
        return this._isEmbedded;
    }
}
